import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { notifications } from "@mantine/notifications";
import {
  insurancePlanTypesService,
  CreateInsurancePlanTypeInput,
  UpdateInsurancePlanTypeInput,
} from "../../services/admin/insurance-plan-types.service";

export function useInsurancePlanTypesAdmin() {
  return useQuery({
    queryKey: ["admin", "insurance-plan-types"],
    queryFn: insurancePlanTypesService.getAll,
  });
}

export function useInsurancePlanType(id?: string) {
  return useQuery({
    queryKey: ["admin", "insurance-plan-types", id],
    queryFn: () => insurancePlanTypesService.getById(id!),
    enabled: !!id,
  });
}

export function useCreateInsurancePlanType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateInsurancePlanTypeInput) =>
      insurancePlanTypesService.create(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance-plan-types"],
      });
      notifications.show({
        title: "Success",
        message: "Insurance plan type created successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to create insurance plan type",
        color: "red",
      });
    },
  });
}

export function useUpdateInsurancePlanType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateInsurancePlanTypeInput) =>
      insurancePlanTypesService.update(input),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance-plan-types"],
      });
      notifications.show({
        title: "Success",
        message: "Insurance plan type updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update insurance plan type",
        color: "red",
      });
    },
  });
}

export function useDeleteInsurancePlanType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => insurancePlanTypesService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance-plan-types"],
      });
      notifications.show({
        title: "Success",
        message: "Insurance plan type deleted successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to delete insurance plan type",
        color: "red",
      });
    },
  });
}

export function useToggleInsurancePlanTypeActive() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      insurancePlanTypesService.toggleActive(id, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["admin", "insurance-plan-types"],
      });
      notifications.show({
        title: "Success",
        message: "Status updated successfully",
        color: "green",
      });
    },
    onError: (error: any) => {
      notifications.show({
        title: "Error",
        message: error.message || "Failed to update status",
        color: "red",
      });
    },
  });
}
